import { App as BaseAntdApp } from 'antd'
import { useEffect, useLayoutEffect } from 'react'
import { I18nextProvider, useTranslation } from 'react-i18next'
import { BrowserRouter } from 'react-router-dom'

import { AnalyticsIdentity, AnalyticsProvider } from './analytics'
import { getConfigInterceptors } from './api/getConfigInterceptors'
import { client as clientMain } from './api/main/client.gen'
import { client as clientSquad } from './api/squad/client.gen'
import { releaseInitialBootScreen } from './components/AppLoadingScreen'
import { ImpersonationBanner } from './components/ImpersonationBanner'
import { DocumentTitleProvider } from './context'
import { UserSessionContextProvider } from './context/UserSessionContextProvider'
import { WorkspacePermissionsProvider } from './context/WorkspacePermissionsProvider'
import { FlagProvider } from './feature-flags'
import { getActiveSquadAppToken } from './features/workspaces/squad/squadAppTokenStore'
import { accountFlagBootstrap } from './flags'
import { ReactQueryProvider } from './ReactQueryProvider'
import { Router } from './router'
import { ThemeProviderWrapper } from './theme'

import 'antd/dist/reset.css'
import './theme/fonts.css'
import './theme/tokens.css'
import './theme/antd-guardrails.css'
import './theme/scrollbars.css'
import './theme/global.css'
import 'src/utils/date'

// Apply env-driven baseURL synchronously at module load, BEFORE React mounts.
// The generated `client.gen.ts` bakes in `http://127.0.0.1:8000` from the
// OpenAPI spec — if we deferred this to a useEffect, the cookie-auth query
// fired by UserSessionContextProvider on first render would race ahead with
// the wrong baseURL, fail, and bounce the user to /login.
const apiBaseUrl = import.meta.env.VITE_APP_CORE_API
if (apiBaseUrl) {
  clientMain.setConfig({ baseURL: apiBaseUrl })
}

const squadBaseUrl = import.meta.env.VITE_APP_SQUAD_API
if (squadBaseUrl) {
  clientSquad.setConfig({ baseURL: squadBaseUrl })
}

// Install interceptors once — they mutate the shared axios instance.
getConfigInterceptors(clientMain?.instance)
getConfigInterceptors(clientSquad?.instance, {
  getSaasAppToken: getActiveSquadAppToken
})

const App = () => {
  const { i18n } = useTranslation()

  useLayoutEffect(releaseInitialBootScreen, [])

  useEffect(() => {
    clientMain.setConfig({
      baseURL: apiBaseUrl,
      headers: {
        'Accept-Language': i18n.language
      }
    })
  }, [i18n.language])

  return (
    <ThemeProviderWrapper>
      <BrowserRouter>
        <BaseAntdApp>
          <ImpersonationBanner />
          {/* Hosts the PostHog client. Safe when no project key is set — the
              client is then never initialised and every helper in
              src/analytics no-ops (see its config.ts). */}
          <AnalyticsProvider>
            {/* Hosts Unleash feature flags for the whole app tree. Safe when the
                frontend API is unconfigured — flags then serve their bootstrap
                defaults (see src/feature-flags). */}
            <FlagProvider appName="account" bootstrap={accountFlagBootstrap}>
              <ReactQueryProvider>
                <UserSessionContextProvider>
                  {/* Serves the effective workspace permissions to the whole
                      tree — the sidebar and route guards read them through
                      useHasWorkspacePermissions. */}
                  <WorkspacePermissionsProvider>
                    <I18nextProvider i18n={i18n}>
                      {/* Sits here rather than beside AnalyticsProvider because it
                        reads both the session and the active language. Renders
                        nothing; it only keeps the analytics identity in step. */}
                      <AnalyticsIdentity />
                      <DocumentTitleProvider>
                        <Router />
                      </DocumentTitleProvider>
                    </I18nextProvider>
                  </WorkspacePermissionsProvider>
                </UserSessionContextProvider>
              </ReactQueryProvider>
            </FlagProvider>
          </AnalyticsProvider>
        </BaseAntdApp>
      </BrowserRouter>
    </ThemeProviderWrapper>
  )
}

export default App
